import React from 'react'
import copy from 'copy-to-clipboard'

import { routes, useFlag } from '@sketch/modules-common'

import { Dropdown, useModalContext } from '@sketch/components'

import { useToast } from '@sketch/toasts'

import {
  ProjectInSidebarAndHeaderFragment,
  TrashedProjectFragment,
  useLeaveProjectMutation,
  WorkspaceMinimalFragment,
  usePinProjectMutation,
  useUnpinProjectMutation,
  GetProjectsDocument,
  GetProjectsQueryVariables,
} from '@sketch/gql-types'

import DeleteProject from '../../modals/DeleteProject'
import DeleteProjectPermanently from '../../modals/DeleteProjectPermanently'
import RestoreProject from '../../modals/RestoreProject'
import { ProjectSharingModal } from '../../modals/ProjectSharingModal'
import { NewProjectSharingModal } from '../../modals/NewProjectSharingModal'
import { CreateCollectionModal } from 'modules/collections/modals'
import { ConfirmRemoveMemberModal } from '../RemoveMemberModal'

interface ProjectProps {
  project: Pick<
    ProjectInSidebarAndHeaderFragment | TrashedProjectFragment,
    'identifier' | 'name' | '__typename' | 'deletedAt'
  >
}

interface ProjectAndWorkspaceProps extends ProjectProps {
  workspaceId: string
}

interface RenameProjectActionProps {
  onRename: () => void
}

interface LeaveProjectActionProps {
  projectIdentifier: string
  projectName: string
}

type PinProjectProps = {
  pinned?: string | null
  projectIdentifier: string
  workspaceId: string
}

const typeInline = <T extends any>(variables: T) => variables

export const CreateCollectionAction = ({
  project,
  workspaceIdentifier,
}: {
  project: ProjectInSidebarAndHeaderFragment
  workspaceIdentifier: string
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() => {
        showModal(CreateCollectionModal, {
          project,
          workspaceIdentifier,
        })
      }}
    >
      Create Collection&hellip;
    </Dropdown.Item>
  )
}

export const LeaveProjectAction: React.FC<LeaveProjectActionProps> = ({
  projectIdentifier,
  projectName,
}) => {
  const { showModal, hideModal } = useModalContext()
  const [leaveProject] = useLeaveProjectMutation({
    variables: { projectIdentifier },
    onError() {
      // onError is used in `<RemoveMemberButton .../>` to handle the case
      // where a user is removing themselves we must show them the confirmation
      // dialog first. All other errors are ignored there.
      //
      // Since we are showing the confirmation dialog in all circumstances (and
      // providing `confirmed: true`) it is not necessary to handle this error
      // case.
      return
    },
  })

  const onConfirm = async () => {
    await leaveProject()
    hideModal()
  }

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(ConfirmRemoveMemberModal, {
          projectName,
          onConfirm,
          onCancelAction: hideModal,
        })
      }}
    >
      Leave&hellip;
    </Dropdown.Item>
  )
}
export const DeleteProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() => {
        showModal(DeleteProject, {
          projectId: project.identifier,
          projectName: project.name,
          workspaceId,
        })
      }}
    >
      Delete&hellip;
    </Dropdown.Item>
  )
}

export const RenameProjectAction: React.FC<RenameProjectActionProps> = ({
  onRename,
}) => <Dropdown.Item onClick={onRename}>Rename&hellip;</Dropdown.Item>

export const CopyProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showToast } = useToast()

  return (
    <Dropdown.Item
      onClick={() => {
        const projectLink = (project.deletedAt
          ? routes.WORKSPACE_TRASH_PROJECT
          : routes.WORKSPACE_PROJECT
        ).create({
          workspaceId,
          projectId: project.identifier,
        })

        copy(`${window.location.origin}${projectLink}`)
        showToast('Link copied')
      }}
    >
      Copy Link
    </Dropdown.Item>
  )
}

export const RestoreProjectAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      onClick={() => showModal(RestoreProject, { project, workspaceId })}
    >
      Restore&hellip;
    </Dropdown.Item>
  )
}

export const DeleteProjectPermanentlyAction: React.FC<ProjectAndWorkspaceProps> = ({
  project,
  workspaceId,
}) => {
  const { showModal } = useModalContext()

  return (
    <Dropdown.Item
      intent="negative"
      onClick={() =>
        showModal(DeleteProjectPermanently, { project, workspaceId })
      }
    >
      Delete Permanently&hellip;
    </Dropdown.Item>
  )
}

export const ShareProjectAction: React.FC<{
  project: ProjectInSidebarAndHeaderFragment
  workspace: WorkspaceMinimalFragment
}> = ({ project, workspace }) => {
  const { showModal } = useModalContext()
  const isNewSharingFFEnabled = useFlag('new-sharing')

  const handleShareClick = () => {
    isNewSharingFFEnabled
      ? showModal(NewProjectSharingModal, {
          project,
          workspace,
        })
      : showModal(ProjectSharingModal, {
          project,
          workspaceId: workspace.identifier,
        })
  }

  return <Dropdown.Item onClick={handleShareClick}>Share&hellip;</Dropdown.Item>
}

export const PinProjectAction = ({
  pinned,
  projectIdentifier,
  workspaceId,
}: PinProjectProps) => {
  const [pinProject] = usePinProjectMutation({
    variables: {
      projectIdentifier,
    },
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
  })

  const [unpinProject] = useUnpinProjectMutation({
    variables: {
      projectIdentifier,
    },
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
  })

  if (pinned) {
    return (
      <Dropdown.Item
        onClick={() => unpinProject({ variables: { projectIdentifier } })}
      >
        Unpin Project
      </Dropdown.Item>
    )
  }

  return (
    <Dropdown.Item
      onClick={() => pinProject({ variables: { projectIdentifier } })}
    >
      Pin Project
    </Dropdown.Item>
  )
}
